/** **`A-21b`(앱) · `W-20b`(웹) 시트가 하는 말과 그 답.** `docs/PRD.md` `7.6.5a` 가 정본이고
 * 목업은 `docs/mockup/ver2-ondora-ui.html` 의 `A-21b` 프레임이다.
 *
 * ## 왜 이 파일이 `packages/shared` 에 있나
 *
 * **앱과 웹이 같은 순간의 같은 문 다른 쪽이다.** 드롭존에 떨어뜨리는 것이 웹의 담기이고
 * (`7.7.3` 의 `W-20b`), 그래서 **제목 · 본문 세 문장 · 버튼 둘 · 배너 두 줄까지 내용이
 * 같다.** `7.7.3` 이 그 자리에 이렇게 적어 뒀다. 배너 끝에 붙던 `판정 기준 미정` 배지는
 * 목업의 설계 표기라 두 화면에서 걷었다(앱 `A-21b` 는 PR #206, 웹 `W-20b` 는 그 뒤 회차).
 * 판정 기준이 미정이라는 사실은 `docs/PRD.md` 오픈 이슈로 남는다.
 *
 * > ⛔ **웹에서 문장을 다시 고르지 않는다.** 한 사실을 두 클라이언트가 두 낱말로 부르면
 * > 그것이 `7.5.9` 규칙 1이 막는 일이다.
 *
 * ⛔ **그래서 이 문자열을 화면 파일에 옮겨 적지 마라.** 두 벌이 되는 순간 `7.7.3a` 가 세 번
 * 적어 둔 사고(앱은 됐고 웹이 남음)가 그대로 난다. 선례는 `remake-messages.ts` ·
 * `domain.ts` 의 `imageLimitSentence` · `jobFullNoticeMessage` · `uploadRejectMessage` 다.
 *
 * ## ⛔ 이 파일은 D1 을 한 칸도 건드리지 않는다
 *
 * 짝인 `precut.ts` 머리말이 이미 못박았다. **`JobImage` 에 칸을 두지 않고 업로드 요청에도
 * 싣지 않는다. 마이그레이션 0건이다**(근거 넷은 `docs/DATA_DICTIONARY.md` **33장**).
 * 이 파일이 더하는 것은 **문구와 판정 하나**이고 DTO 도 바뀌지 않는다.
 *
 * ## ⭐ 이 파일의 핵심은 문자열이 아니라 **두 답이 서로 반대라는 것**이다
 *
 * | 무엇 | 답 |
 * |---|---|
 * | `precutSheetActions().primary` | **`KEEP`**(`그대로 담기`) |
 * | `precutSheetDismissOutcome()` | **`REPICK`**(`다시 고르기`) |
 *
 * **둘이 반대인 것이 설계다.** 근거는 각 함수 주석에 있고, ⛔ **정리 회차에 "둘 다 `KEEP`
 * 이면 간단한데"로 합치지 마라.** 합치는 순간 **안 읽고 닫은 것이 돈이 되는 선택**이 된다.
 *
 * ## 낱말
 *
 * ⛔ **`누끼`는 금지어다**(`7.5.9`). 낱말은 **`배경 지우기`** 하나다. ⛔ 그리고
 * `PRECUT`·`NOT_PRECUT`·`UNKNOWN` 은 **코드 전용 코드라 셀러 화면에 나가지 않는다**
 * (`precut.ts` 머리말).
 */

import { countPrecut, type PrecutVerdict } from "./precut";
import { CUTOUT_UNIT_PRICE_KRW } from "./cost";
import { formatKrw } from "./format";

/** 셀러가 이 시트에 내놓을 수 있는 답. **둘뿐이다.**
 *
 * ⛔ **셋째 값(`걸린 장만 빼고 담기`)을 여기 더하지 마라.** `7.6.5a` 의 "여기선 아직 못
 * 합니다"가 그 길을 **버튼이 셋이면 240px 한 줄에 서지 못한다**는 계산으로 막아 뒀고,
 * 줄을 하나 더 쓸지는 `7.13` **D-89** 가 볼 일이다. 타입에 먼저 값을 넣으면 화면이 그릴 수
 * 없는 답이 계약에 생긴다. */
export type PrecutSheetOutcome =
  /** `그대로 담기`. 고른 장을 **전부** 담는다. */
  | "KEEP"
  /** `다시 고르기`. **한 장도 담지 않고** 그리드로 돌아간다. 고른 상태는 그대로 남는다. */
  | "REPICK";

/** **시트가 뜨는가.** 뜨는 조건이 곧 `countPrecut(...) > 0` 하나다(`7.6.5a`).
 *
 * ⛔ **`UNKNOWN` 을 세지 마라. 이 함수는 세지 않는다** — `countPrecut` 이 `PRECUT` 만
 * 센다. 여기서 `measured !== null` 이나 「알파가 있는가」로 **다시 세면 거짓 양성이
 * 쏟아지고**, `precut.ts` 가 자격(머리)과 근거(실제 알파) **두 층으로 판정한 이유가 통째로
 * 무너진다.** 못 띄운 값은 셀러가 한 장 값을 더 쓰는 것이고, **잘못 띄운 값은 시트 전체를
 * 못 믿게 되는 것**이다. 뒤엣것이 비싸다(`precut.ts` 머리말).
 *
 * ⛔ **판정을 이 함수 안에 다시 쓰지 마라. 한 줄 위임이다.**
 *
 * ⚠️ **`docs/DATA_DICTIONARY.md` 33.6 이 「시트를 띄울까」 불리언 함수를 두지 않기로 적어
 * 뒀고, 2026-09-25 에 그 금지를 「두 번째 **구현**을 두지 않는다」로 좁혔다.** 이 함수는
 * 구현이 아니라 이름이고 본문이 `countPrecut` 한 줄이라 갈릴 자리가 없다. 경위는 같은
 * 문서 33.8 에 남겼다. */
export function precutSheetOpens(assessments: readonly { verdict: PrecutVerdict }[]): boolean {
  return countPrecut(assessments) > 0;
}

/** **제목**(목업 `.st`).
 *
 * **수는 판정이 낸 값이다.** 1장이면 `1장`이다. ⛔ **`여러 장`으로 뭉치지 않는다** —
 * **셀러가 몇 장을 다시 고를지 정하는 데 그 수가 필요하다**(`7.6.5a`).
 *
 * ⚠️ **`count < 1` 이면 던진다. 화면 문자열로 덮지 않는다.** `formatKrw` 와 같은 판단이다.
 * `count` 는 `countPrecut` 이 낸 정수이고, `0` 이면 애초에 시트가 뜨지 않으므로
 * (`precutSheetOpens`) **여기 닿는 `0` 은 세상의 상태가 아니라 계약 위반**이다. 계약 위반은
 * 셀러가 아니라 우리가 봐야 하고, 던지면 개발·CI 에서 걸린다. */
export function precutSheetTitle(count: number): string {
  assertSheetCount(count, "precutSheetTitle");
  return `배경이 이미 지워진 사진이 ${count}장 있어요`;
}

/** **본문**(목업 `.sb`) **세 문장. 순서가 곧 우선순위다.**
 *
 * 배열로 돌려주는 이유: 앱은 바닥 시트고 웹은 드롭존 위 모달이라(`7.7.3` 의 `W-20b`)
 * **줄을 어떻게 흘릴지는 화면이 정한다.** 문장이 무엇이고 어느 순서인가만 여기서 잠근다.
 *
 * | # | 무엇 | ⛔ 빼면 |
 * |---|---|---|
 * | 1 | **돈.** 장당 단가가 첫 문장이다 | 셀러가 **결제 화면에서 처음 안다** |
 * | 2 | **결과가 나아지지 않을 수 있다** | 셀러가 **쓸 만하던 장을 잃고 나서야 안다**(`A-25` 가 같은 구멍을 이미 겪었다) |
 * | 3 | **그래도 담으세요** | 아래 ⭐ |
 *
 * ⭐ **셋째 문장을 지우지 마라.** `7.6.5a`: **"이 문장이 이 시트의 성격을 정한다. 앞 두
 * 문장만 두면 알림이 아니라 경고가 되고, 경고는 사용자가 받기로 한 길을 막는다."** 뒷절
 * (`잘못 짚은 것이어도 그대로 담으시면 됩니다.`)은 **판정이 틀렸을 때의 출구**이고, 이것이
 * 있어서 앞 두 문장이 단정해도 값이 싸다.
 *
 * ⛔ **금액을 문자열에 박지 마라.** 단가는 `CUTOUT_UNIT_PRICE_KRW` 에서 나온다. 청구가가
 * 움직이는 날 이 문장만 옛 수를 말하게 된다. */
export function precutSheetBody(count: number): readonly string[] {
  assertSheetCount(count, "precutSheetBody");
  return [
    `이 ${count}장도 배경 지우기를 한 번 더 합니다. 장당 ${formatKrw(CUTOUT_UNIT_PRICE_KRW)}으로 나머지 장과 똑같이 듭니다.`,
    "이미 지워진 경계를 한 번 더 깎게 되어, 지금 사진보다 결과가 나아지지 않을 수 있습니다.",
    "다른 도구로 지운 결과가 마음에 들지 않아 다시 하시는 것이라면 그대로 담으세요. 잘못 짚은 것이어도 그대로 담으시면 됩니다.",
  ];
}

/** **제한 배너**(목업 `.limit`) 두 줄 + 배지 값.
 *
 * **배지는 `있는 일의 모르는 숫자`에 붙인다**(`A-24b` 가 세운 규칙). **여기는 있는 화면의
 * 모르는 기준**이라 그 규칙 안이다.
 *
 * ⚠️ 다만 미정 배지는 목업의 설계 표기라 셀러 화면에서는 걷었다(앱 `A-21b` · 웹 `W-20b`).
 * 화면이 그리는 것은 `l1` · `l2` 두 줄뿐이고, `badge` 는 두 화면이 모두 걷힌 뒤 읽는 곳이
 * 없다. 값을 지울지는 `data-manager` 판단이라 이 회차는 주석만 맞췄다.
 *
 * ⭐ **판정이 서면 이 배너가 통째로 사라진다.** 셀러 화면에 우리 사정이 남지 않는다.
 * ⚠️ 그날 지우는 것은 **이 함수와 그 호출부 둘 다**이고, 제목·본문·버튼은 그대로 남는다. */
export function precutSheetLimit(): { l1: string; l2: string; badge: string } {
  return {
    l1: "무엇을 보고 이렇게 판단하는지는 아직 정해지지 않았습니다.",
    l2: "그 기준이 서기 전까지 이 시트는 뜨지 않습니다.",
    badge: "판정 기준 미정",
  };
}

/** **버튼 둘.** 화면에서 **좌 `secondary`(ghost) · 우 `primary`** 로 선다(목업 `.row`).
 *
 * ⭐ **primary 가 `그대로 담기` 인 것은 결정이다.** `7.6.5a`: **"사용자가 받기로 했으므로
 * 기본값이 받는 쪽이어야 한다. primary 를 물러나는 쪽에 두면 화면이 결정과 반대로 말한다."**
 * 사용자가 확정한 것은 **① 이미 배경이 지워진 사진도 받는다 ② 알림창을 하나 띄운다** 둘이고
 * (`13.1` **O-24**), **받는 것이 결정이므로 이 화면은 막는 화면이 아니라 알리는 화면이다.**
 * ⛔ **둘을 바꾸지 마라.**
 *
 * ⛔ **버튼 라벨에 수를 넣지 마라.** `n장 담기`는 `A-21` 의 라벨이고, **같은 낱말에 다른 수가
 * 붙으면 셀러가 두 버튼을 같은 것으로 읽는다**(`7.6.5a`).
 *
 * **`다시 고르기`는 아무것도 담지 않고 그리드로 돌아간다. 고른 상태는 그대로 남는다** —
 * 다시 고르라고 해 놓고 고른 것을 지우면 셀러가 처음부터 다시 한다. ⚠️ 그 동작은 화면
 * 소관이고 이 파일은 **그 버튼이 어느 답을 내는가**까지만 잠근다. */
export function precutSheetActions(): {
  primary: { label: string; outcome: PrecutSheetOutcome };
  secondary: { label: string; outcome: PrecutSheetOutcome };
} {
  return {
    primary: { label: "그대로 담기", outcome: "KEEP" },
    secondary: { label: "다시 고르기", outcome: "REPICK" },
  };
}

/** **백드롭 탭 · 시스템 뒤로 · 내비 `✕` 가 하는 일.**
 *
 * ⭐ **`REPICK` 인 것은 결정이다.** `7.6.5a`: **"백드롭 탭과 뒤로가기는 `다시 고르기` 와
 * 같은 일을 한다. 담지 않고 닫는다. ⛔ 닫는 동작이 담는 쪽으로 떨어지게 만들지 않는다 —
 * 그러면 셀러가 안 읽고 닫은 것이 돈이 되는 선택이 된다."** `7.5.8` 이 잠근 시트 규칙
 * 그대로이고, **명시적 확인(`그대로 담기`)만 담는다.**
 *
 * ⭐ **그래서 이 답은 `precutSheetActions().primary.outcome` 과 반대다. 그것이 정상이다.**
 * ⛔ **둘을 같은 값으로 합치지 마라.** 파일 머리말의 표를 본다.
 *
 * ⚠️ **안드로이드 하드웨어 뒤로·제스처 뒤로도 이 답이다.** 화면에서 글리프를 없애도 OS 가
 * 주는 길은 남으므로 **구현이 그 길을 같은 동작으로 잡아야 한다**(`7.6.6` 이 `A-22` 에서
 * 같은 판단을 했다). 잡지 않으면 OS 뒤로만 담는 쪽으로 떨어져 위 금지를 그대로 어긴다. */
export function precutSheetDismissOutcome(): PrecutSheetOutcome {
  return "REPICK";
}

/** **그 답이 담는 쪽인가.**
 *
 * 화면이 `outcome === "KEEP"` 을 직접 쓰지 않게 하는 자리다. ⛔ **문자열 비교를 화면마다
 * 적으면**, 셋째 답이 서는 날(`7.13` **D-89** 의 `걸린 장만 빼고 담기`) **앱과 웹이 각자
 * 다른 기본값으로 떨어진다.** 지금 답이 둘뿐이라고 `!== "REPICK"` 으로 쓰지도 마라 —
 * 그것이 정확히 셋째 값이 생기는 날 조용히 뒤집히는 모양이다. */
export function precutSheetKeeps(outcome: PrecutSheetOutcome): boolean {
  return outcome === "KEEP";
}

/** `count` 계약 검사. **`formatKrw` 와 같은 이유로 던진다**(그 함수 주석에 근거가 길게 있다).
 *
 * 이 수의 출처는 `countPrecut` 하나이고 그것은 **0 이상의 정수**다. `0` 은 시트가 안 뜨는
 * 값이라 문구 함수에 닿을 수 없고, 정수가 아닌 값은 이 패키지에 만들 경로가 없다. */
function assertSheetCount(count: number, fn: string): void {
  if (!Number.isInteger(count) || count < 1) {
    throw new TypeError(`${fn}: 걸린 장의 수가 1 이상의 정수가 아니다 (${String(count)})`);
  }
}
